import * as React from "react";
import { FaqProps } from "./typings";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import { observer, inject } from "mobx-react";
import { UitkExpandoList, UitkExpandoListItem } from "uitk-react-expando";
import {
  FlexTrackingInfo,
  Action,
  sendDelayedClickstreamTrackEvent,
} from "src/components/utility/analytics/FlexAnalyticsUtils";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading } from "uitk-react-text";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { FrequentlyAskedQuestionsFlexModuleResult } from "typings/microserviceModels/frequently-asked-questions-flex-module";
import { UitkSecondaryButton } from "uitk-react-button";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";

/**
 * Frequently asked questions module that allows users to gather
 * more information on their desired destinations
 */
export const Faq = inject(
  "markdown",
  "flexModuleModelStore"
)(
  observer((props: FaqProps) => {
    const { flexTemplateNode, markdown, flexModuleModelStore, context } = props;

    if (!flexTemplateNode || !markdown || !flexModuleModelStore) {
      return null;
    }

    const { id, options } = flexTemplateNode;
    const { fmId, fmTitleId } = options;
    const model = flexModuleModelStore.getModel(id) as FrequentlyAskedQuestionsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { faqs, displayThreshold, structuredData } = model;
    const itemKeyHelper = new ItemKeyHelper("faq");
    const { formatText } = useLocalization();

    const initialDisplayThreshold = displayThreshold || faqs?.length;
    const [numberOfFaqsToShow, setNumberOfFaqsToShow] = React.useState(initialDisplayThreshold);
    const [isCollapsed, setIsCollapsed] = React.useState(true);

    if (!faqs || faqs.length === 0) {
      return null;
    }

    const clickHandler = () => {
      const toSet = numberOfFaqsToShow === initialDisplayThreshold ? faqs.length : initialDisplayThreshold;
      setNumberOfFaqsToShow(toSet);
      setIsCollapsed(!isCollapsed);
    };

    const faqsCompiled = faqs.map((faq) => {
      return {
        ...faq,
        answer: markdown.compileMarkdown(faq.question, faq.answer),
      };
    });

    return (
      <LazyLoad context={context}>
        <div
          className="Faq uitk-cell all-cell-1-1 all-b-padding-three all-y-padding-three"
          id={id}
          data-fm={fmId}
          data-fm-title-id={fmTitleId}
        >
          <SchemaOrgScript structuredData={structuredData} />
          <UitkCardAloha>
            <UitkCardAlohaContentSection key={itemKeyHelper.next()}>
              <UitkHeading level={2} size={600}>
                {formatText("frequentlyAskedQuestions.title")}
              </UitkHeading>
            </UitkCardAlohaContentSection>
            <UitkExpandoList className="faqExpando">
              {faqsCompiled.slice(0, numberOfFaqsToShow).map((faq, index) => (
                <Expando
                  key={itemKeyHelper.next()}
                  callToActionText={faq.question}
                  expandedText={faq.answer}
                  data-testid={`faq-${index}`}
                  index={index}
                  sectionId={faq.sectionId}
                />
              ))}
            </UitkExpandoList>
            {initialDisplayThreshold < faqs.length && (
              <UitkCardAlohaContentSection>
                <UitkSecondaryButton
                  isFullWidth={true}
                  onClick={clickHandler}
                  text={isCollapsed ? formatText("propertyfilter.seeMore") : formatText("propertyfilter.seeLess")}
                />
              </UitkCardAlohaContentSection>
            )}
          </UitkCardAloha>
        </div>
      </LazyLoad>
    );
  })
);

interface ExpandoProps extends React.HTMLAttributes<HTMLDivElement> {
  callToActionText: string;
  expandedText: string | undefined;
  index: number;
  sectionId: number;
}
/**
 * This is a simple expander that when clicked, will show more text
 * TODO: Replace with UITK component when it has the design we need
 * @param callToActionText - Text that the user sees regardless of expansion
 * @param expandedText - Text that the user sees once expanded
 */
function Expando({ callToActionText, expandedText, index, sectionId }: ExpandoProps) {
  const track = useClickTracker();

  if (expandedText === undefined) {
    return null;
  }

  const [isVisible, setIsVisible] = React.useState(false);

  const visibilityStatus = isVisible ? "close" : "open";
  const trackingReferrer =
    sectionId === null ? `question.${index}.${visibilityStatus}` : `question.${index}.${sectionId}.${visibilityStatus}`;

  const callback = React.useCallback(() => {
    const flexTracking: FlexTrackingInfo = {
      moduleName: "faq",
      action: Action.CLICK,
      rfrr: trackingReferrer,
    };
    sendDelayedClickstreamTrackEvent(flexTracking, track);
    setIsVisible(!isVisible);
  }, [isVisible]);

  return (
    <UitkExpandoListItem
      isVisible={false}
      expandoTitle={callToActionText}
      expandoDescription={<div className="faqText" dangerouslySetInnerHTML={{ __html: expandedText }} />}
      analytics={{
        id: trackingReferrer,
        callback,
      }}
    />
  );
}

export default Faq;
